import { Box, Table, TableBody, TableContainer, TableHead, TableRow } from "@mui/material";
import TextFieldComponent from "components/input/TextFieldComponent";
import TableCellComponent from "components/table/TableCellComponent";
import TableRowComponent from "components/table/TableRowComponent";
import { HEADER_TIRE_OPTIONS } from "constants/data";
import { useState } from "react";
import { MESSAGE } from "constants/validate";
import { MstItemPructureProtectUpdate, PunctureProtection } from "../types/option";

interface TireOptionTableProps {
  data: PunctureProtection[];
  setData: (data: PunctureProtection[]) => void;
  onUpdateItem: (data: MstItemPructureProtectUpdate) => Promise<void>;
}

/**
 * Renders a table displaying tire options.
 */
export default function TireOptionTable({ data, setData, onUpdateItem }: TireOptionTableProps) {
  const [errors, setErrors] = useState<{ [key: string]: boolean }>({});

  const onChangeItem = (index: number, key: string, value: any) => {
    const options = [...data];
    options[index] = {
      ...options[index],
      [key]: value,
    };
    setData(options);
  };

  // const onBlur = async (index: number) => {
  //   await onUpdateItem({
  //     id: data[index].id,
  //     puncture_protection_name: data[index].puncture_protection_name,
  //     coverage_amount_low: Number(data[index].coverage_amount_low),
  //     coverage_amount_high: Number(data[index].coverage_amount_high),
  //     coverage_enrollment_fee: Number(data[index].coverage_enrollment_fee),
  //   })
  // };

  const onBlur = async (index: number, key: string, value: any) => {
    // Only call the update if no errors
    if (Object.keys(errors).length === 0 || Object.values(errors).every((error) => !error)) {
      await onUpdateItem({
        id: data[index].id,
        puncture_protection_name: data[index].puncture_protection_name,
        coverage_amount_low: Number(data[index].coverage_amount_low),
        coverage_amount_high: Number(data[index].coverage_amount_high),
        coverage_enrollment_fee: Number(data[index].coverage_enrollment_fee),
      });
    }
  };

  return (
    <TableContainer
      component={Table}
      sx={{
        width: '100%',
        boxShadow: "none",
        borderCollapse: "unset",
        borderSpacing: "1px",
        minWidth: 870
      }}
    >
      <TableHead>
        <TableRowComponent data={HEADER_TIRE_OPTIONS} type="header" />
      </TableHead>
      <TableBody sx={{ boxShadow: "none" }}>
        {data.map((item, index) => (
          <TableRow key={item.id} sx={{ borderBottom: "none" }}>
            <TableCellComponent sx={{ border: "none", p: 0 }}>
              {/* <Tooltip title={errors[`${index}-puncture_protection_name`] ?? ""} followCursor> */}
              <TextFieldComponent
                type="string"
                value={item.puncture_protection_name}
                sxInput={{ fontSize: {xs: '16px', lg: "20px"}, height: "42px" }}
                sxRoot={{
                  borderRadius: 0,
                  width: "100%",
                  border: !errors[`${index}-puncture_protection_name`]
                    ? "2px solid var(--silver-250)"
                    : "2px solid var(--red-800)",
                }}
                sxNotchedOutline={{ border: "none" }}
                onChangeText={(value) =>
                  onChangeItem && onChangeItem(index, "puncture_protection_name", value)
                }
                onBlur={() =>
                  onBlur(index, "puncture_protection_name", item.puncture_protection_name)
                }
                validateOption={{
                  required: {
                    message: MESSAGE.EMPTY,
                  },
                  maxLength: {
                    message: MESSAGE.MAX_LENGTH(50),
                    value: 50,
                  },
                }}
                isHiddenError
                onError={(error) => {
                  setErrors((prev) => ({
                    ...prev,
                    [`${index}-puncture_protection_name`]: error,
                  }));
                  if (!error) {
                    const updatedErrors = { ...errors };
                    delete updatedErrors[`${index}-puncture_protection_name`];
                    setErrors(updatedErrors);
                  }
                }}
                // onBlur={() => onUpdateItem && onUpdateItem(index, "maker_name")}
              />
              {/* </Tooltip> */}
            </TableCellComponent>
            <TableCellComponent
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                padding: 0,
                borderBottom: "none",
              }}
            >
              {/* <Tooltip title={errors[`${index}-coverage_amount_low`] ?? ""} followCursor> */}
              <TextFieldComponent
                type="text"
                value={item.coverage_amount_low}
                sxInput={{ fontSize: {xs: '16px', lg: "20px"}, height: "42px", textAlign: "center" }}
                sxRoot={{
                  borderRadius: 0,
                  width: "100%",
                  border: !errors[`${index}-coverage_amount_low`]
                    ? "2px solid var(--silver-250)"
                    : "2px solid var(--red-800)",
                }}
                sxNotchedOutline={{ border: "none" }}
                onBlur={() => onBlur(index, "coverage_amount_low", item.coverage_amount_low)}
                onInputText={(e) => {
                  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                  onChangeItem &&
                    onChangeItem(
                      index,
                      "coverage_amount_low",
                      e.target.value.replace(/[^0-9]/g, ""),
                    );
                }}
                isHiddenError
                validateOption={{
                  required: {
                    message: MESSAGE.EMPTY,
                  },
                  maxLength: {
                    message: MESSAGE.MAX_LENGTH(9),
                    value: 9,
                  },
                }}
                onError={(error) => {
                  setErrors((prev) => ({
                    ...prev,
                    [`${index}-coverage_amount_low`]: error,
                  }));
                  if (!error) {
                    const updatedErrors = { ...errors };
                    delete updatedErrors[`${index}-coverage_amount_low`];
                    setErrors(updatedErrors);
                  }
                }}
                // onBlur={() => onUpdateItem && onUpdateItem(index, "maker_name")}
              />
              {/* </Tooltip> */}
              <Box mx={1}>〜</Box>
              {/* <Tooltip title={errors[`${index}-coverage_amount_high`] ?? ""} followCursor> */}
              <TextFieldComponent
                type="string"
                value={item.coverage_amount_high}
                sxInput={{ fontSize: {xs: '16px', lg: "20px"}, height: "42px", textAlign: "center" }}
                sxRoot={{
                  borderRadius: 0,
                  width: "100%",
                  border: !errors[`${index}-coverage_amount_high`]
                    ? "2px solid var(--silver-250)"
                    : "2px solid var(--red-800)",
                }}
                sxNotchedOutline={{ border: "none" }}
                onBlur={() => onBlur(index, "coverage_amount_high", item.coverage_amount_high)}
                onInputText={(e) => {
                  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                  onChangeItem &&
                    onChangeItem(
                      index,
                      "coverage_amount_high",
                      e.target.value.replace(/[^0-9]/g, ""),
                    );
                }}
                isHiddenError
                validateOption={{
                  required: {
                    message: MESSAGE.EMPTY,
                  },
                  maxLength: {
                    message: MESSAGE.MAX_LENGTH(9),
                    value: 9,
                  },
                }}
                onError={(error) => {
                  setErrors((prev) => ({
                    ...prev,
                    [`${index}-coverage_amount_high`]: error,
                  }));
                  if (!error) {
                    const updatedErrors = { ...errors };
                    delete updatedErrors[`${index}-coverage_amount_high`];
                    setErrors(updatedErrors);
                  }
                }}
                // onBlur={() => onUpdateItem && onUpdateItem(index, "maker_name")}
              />
              {/* </Tooltip> */}
            </TableCellComponent>
            <TableCellComponent sx={{ border: "none", p: 0 }}>
              {/* <Tooltip title={errors[`${index}-coverage_enrollment_fee`] ?? ""} followCursor> */}
              <TextFieldComponent
                type="string"
                value={item.coverage_enrollment_fee}
                sxInput={{ fontSize: {xs: '16px', lg: "20px"}, height: "42px", textAlign: "center" }}
                sxRoot={{
                  borderRadius: 0,
                  width: "100%",
                  border: !errors[`${index}-coverage_enrollment_fee`]
                    ? "2px solid var(--silver-250)"
                    : "2px solid var(--red-800)",
                }}
                sxNotchedOutline={{ border: "none" }}
                onInputText={(e) => {
                  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                  onChangeItem &&
                    onChangeItem(
                      index,
                      "coverage_enrollment_fee",
                      e.target.value.replace(/[^0-9]/g, ""),
                    );
                }}
                onBlur={() =>
                  onBlur(index, "coverage_enrollment_fee", item.coverage_enrollment_fee)
                }
                validateOption={{
                  required: {
                    message: MESSAGE.EMPTY,
                  },
                  minLength: {
                    value: 3,
                    message: "",
                  },
                  numericFormat: {
                    precision: 9,
                    scale: 0,
                    message: "",
                  },
                }}
                isHiddenError
                onError={(error) => {
                  setErrors((prev) => ({
                    ...prev,
                    [`${index}-coverage_enrollment_fee`]: error,
                  }));
                  if (!error) {
                    const updatedErrors = { ...errors };
                    delete updatedErrors[`${index}-coverage_enrollment_fee`];
                    setErrors(updatedErrors);
                  }
                }}
              />
              {/* </Tooltip> */}
            </TableCellComponent>
          </TableRow>
        ))}
      </TableBody>
    </TableContainer>
  );
}
